import React, { useState } from 'react';
import { Dropdown, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

import { exportApi } from 'api';
import downloadStockLedgerXlsx from 'components/add_inventory/stock-ledger/downloadStockLedgerXlsx';

import { TIME_OPTION } from 'constants/time';

// ledger exports are rendered client-side into a two-sheet Excel file
const LEDGER_EXPORT_TITLE = 'Inventory Ledger';

const ExportDropdown = () => {
  const [loading, setLoading] = useState(false);
  const [exports, setExports] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);
  // ledger items that failed Excel generation; their next click downloads raw CSV
  const [csvFallbackIds, setCsvFallbackIds] = useState(() => new Set());

  const fetchExports = async () => {
    try {
      setLoading(true);
      const data = await exportApi.getList();

      setExports(data.results);
    } catch (error) {
      toast.error('Failed to load exports');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async exportItem => {
    if (
      exportItem.title !== LEDGER_EXPORT_TITLE ||
      csvFallbackIds.has(exportItem.id)
    ) {
      // opened synchronously within the click so user activation is valid
      window.open(exportItem.file, '_blank', 'noopener');
      return;
    }

    if (downloadingId) return;
    try {
      setDownloadingId(exportItem.id);
      const hasData = await downloadStockLedgerXlsx(exportItem.file);
      if (!hasData) {
        toast.warning('The export contains no stock movements.');
      }
    } catch (error) {
      // window.open here could be popup-blocked (the click's user activation
      // expires during the awaits), so route the next click to the raw CSV
      setCsvFallbackIds(prev => new Set(prev).add(exportItem.id));
      toast.error(
        'Failed to generate the Excel file. Click download again to get the raw CSV.',
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const getStatusIcon = status => {
    switch (status) {
      case 'completed':
        return (
          <FontAwesomeIcon icon="check-circle" className="text-success me-2" />
        );
      case 'scheduled':
        return <FontAwesomeIcon icon="clock" className="text-warning me-2" />;
      case 'failed':
        return (
          <FontAwesomeIcon icon="times-circle" className="text-danger me-2" />
        );
      default:
        return null;
    }
  };

  return (
    <Dropdown
      navbar={true}
      as="li"
      onToggle={isOpen => isOpen && fetchExports()}
    >
      <Dropdown.Toggle bsPrefix="toggle" variant="link" className="nav-link">
        <FontAwesomeIcon icon="download" className="fs-8" />
      </Dropdown.Toggle>

      <Dropdown.Menu
        className="dropdown-caret dropdown-menu-card dropdown-menu-end"
        style={{ width: '280px' }}
      >
        <div className="bg-white rounded-2 py-2 dark__bg-1000">
          <div className="px-3 py-2 d-flex justify-content-between align-items-center border-bottom">
            <h5 className="mb-0 fs-0">Exports</h5>
            <Button
              variant="link"
              size="sm"
              className="p-0"
              onClick={fetchExports}
              disabled={loading}
            >
              <FontAwesomeIcon icon="sync-alt" spin={loading} />
            </Button>
          </div>

          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {loading ? (
              <div className="text-center py-3">
                <FontAwesomeIcon icon="sync-alt" spin className="me-2" />
                Loading...
              </div>
            ) : exports.length === 0 ? (
              <div className="text-center py-3">No exports available</div>
            ) : (
              <div className="list-group list-group-flush">
                {exports.map(exportItem => (
                  <div
                    key={exportItem.id}
                    className="dropdown-item d-flex justify-content-between align-items-center"
                  >
                    {getStatusIcon(exportItem.status)}

                    <div className="flex-grow-1">
                      <p className="mb-0 fs--1">{exportItem.title}</p>
                      <div className="fs-11 text-700">
                        {exportItem.record_count} records
                      </div>
                      <small className="text-700">
                        {new Date(exportItem.created).toLocaleString(
                          'en-IN',
                          TIME_OPTION,
                        )}
                      </small>
                    </div>

                    {exportItem.status === 'completed' ? (
                      <Button
                        variant="falcon-default"
                        size="sm"
                        disabled={downloadingId === exportItem.id}
                        onClick={() => handleDownload(exportItem)}
                      >
                        <FontAwesomeIcon
                          icon={
                            downloadingId === exportItem.id
                              ? 'sync-alt'
                              : 'download'
                          }
                          spin={downloadingId === exportItem.id}
                        />
                      </Button>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ExportDropdown;
